import { axiosInstance } from './Api';

const authHeaders = () => {
    const token = localStorage.getItem('jwt_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeAdminUser = (payload) => {
    const entity = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
    if (!entity || Array.isArray(entity) || typeof entity !== 'object') return entity;
    return {
        ...entity,
        phone_number: entity.phone_number ?? entity.phoneNumber ?? '',
        phone_country_id: entity.phone_country_id ?? entity.phone_country_code ?? null,
        country_id: entity.country_id ?? entity.country_code ?? null,
    };
};

const normalizeAdminUserList = (payload) => {
    if (Array.isArray(payload)) {
        return { data: payload.map(normalizeAdminUser), meta: { total: payload.length, current_page: 1, last_page: 1 } };
    }

    if (!payload || typeof payload !== 'object') {
        return { data: [], meta: { total: 0, current_page: 1, last_page: 1 } };
    }

    const derivedMeta = payload.meta || {
        total: payload.total ?? 0,
        current_page: payload.current_page ?? 1,
        last_page: payload.last_page ?? 1,
        per_page: payload.per_page ?? (Array.isArray(payload.data) ? payload.data.length : 0),
    };

    return {
        ...payload,
        data: Array.isArray(payload.data) ? payload.data.map(normalizeAdminUser) : [],
        meta: derivedMeta,
    };
};

export const fetchAdminDashboard = async () => {
    const { data } = await axiosInstance.get('/admin/dashboard');
    return data;
};

export const fetchAdminOverviewHealth = async () => {
    const { data } = await axiosInstance.get('/admin/overview/health');
    return data;
};

export const fetchAdminOverviewDashboard = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/overview/dashboard', { params });
    return data;
};

export const fetchAdminNotificationLogs = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/notification-logs', { params });
    return data;
};

export const fetchAdminNotificationLogStats = async () => {
    const { data } = await axiosInstance.get('/admin/notification-logs/stats');
    return data;
};

export const fetchAdminNotificationDeviceUsers = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/notification-logs/users', { params });
    return data;
};

export const fetchAdminTransactions = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/transactions', { params });
    return data;
};

export const fetchAdminTransaction = async (id) => {
    const { data } = await axiosInstance.get(`/admin/transactions/${id}`);
    return data;
};

export const fetchAdminTransactionStats = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/transactions/stats', { params });
    return data;
};

export const fetchAdminTransactionDemographics = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/transactions/demographics', { params });
    return data;
};

export const fetchAdminTransactionProgress = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/transactions/progress', { params });
    return data;
};

export const fetchAdminPreparedTransactions = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/transactions/prepared', { params });
    return data;
};

export const fetchAdminFailedTransactions = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/transactions/failed', { params });
    return data;
};

export const fetchAdminRevenueSummary = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/revenue/summary', { params });
    return data;
};

export const fetchAdminRevenueBreakdown = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/revenue/breakdown', { params });
    return data;
};

export const fetchAdminRevenueByOrganization = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/revenue/by-organization', { params });
    return data;
};

export const fetchAdminRevenueByIntegration = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/revenue/by-integration', { params });
    return data;
};

export const fetchAdminReconciliation = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/revenue/reconciliation', { params });
    return data;
};

export const fetchAdminBillingProfiles = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/billing/profiles', { params });
    return data;
};

export const fetchAdminPlanOffers = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/plan-offers', { params });
    return data;
};

export const updateAdminPlanVisibility = async (code, isListed) => {
    const { data } = await axiosInstance.put(`/admin/plan-offers/plans/${code}/visibility`, { is_listed: isListed });
    return data;
};

export const setAdminPartnerPlan = async (organizationId, enabled) => {
    const { data } = await axiosInstance.put(`/admin/plan-offers/organizations/${organizationId}/partner`, { enabled });
    return data;
};

export const saveAdminPlanOffer = async (payload) => {
    const { data } = await axiosInstance.post('/admin/plan-offers', payload);
    return data;
};

export const updateAdminPublicPlan = async (code, payload) => {
    const { data } = await axiosInstance.put(`/admin/plan-offers/plans/${code}`, payload);
    return data;
};

export const deactivateAdminPlanOffer = async (id) => {
    const { data } = await axiosInstance.delete(`/admin/plan-offers/${id}`);
    return data;
};

export const fetchAdminBillingProfile = async (organizationId) => {
    const { data } = await axiosInstance.get(`/admin/billing/profiles/${organizationId}`);
    return data;
};

export const fetchAdminBills = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/billing/bills', { params });
    return data;
};

export const fetchAdminBill = async (id, params = {}) => {
    const { data } = await axiosInstance.get(`/admin/billing/bills/${id}`, { params });
    return data;
};

export const fetchAdminInvoices = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/billing/invoices', { params });
    return data;
};

export const fetchAdminInvoice = async (id, params = {}) => {
    const { data } = await axiosInstance.get(`/admin/billing/invoices/${id}`, { params });
    return data;
};

export const fetchAdminContracts = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/contracts', { params });
    return data;
};

export const fetchAdminContract = async (id) => {
    const { data } = await axiosInstance.get(`/admin/contracts/${id}`);
    return data;
};

export const updateAdminContract = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/contracts/${id}`, payload);
    return data;
};

export const fetchAdminAlerts = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/alerts', { params });
    return data;
};

export const acknowledgeAdminAlert = async (id) => {
    const { data } = await axiosInstance.post(`/admin/alerts/${id}/acknowledge`);
    return data;
};

export const resolveAdminAlert = async (id) => {
    const { data } = await axiosInstance.post(`/admin/alerts/${id}/resolve`);
    return data;
};

export const fetchAdminFreshness = async () => {
    const { data } = await axiosInstance.get('/admin/freshness');
    return data;
};

export const fetchAdminAuditActions = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/audit/actions', { params });
    return data;
};

export const fetchAdminSavedViews = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/saved-views', { params });
    return data;
};

export const createAdminSavedView = async (payload) => {
    const { data } = await axiosInstance.post('/admin/saved-views', payload);
    return data;
};

export const updateAdminSavedView = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/saved-views/${id}`, payload);
    return data;
};

export const deleteAdminSavedView = async (id) => {
    const { data } = await axiosInstance.delete(`/admin/saved-views/${id}`);
    return data;
};

export const fetchAdminUsers = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/users', { params });
    return normalizeAdminUserList(data);
};

export const fetchAdminUser = async (id) => {
    const { data } = await axiosInstance.get(`/admin/users/${id}`);
    return normalizeAdminUser(data);
};

export const createAdminUser = async (payload) => {
    const { data } = await axiosInstance.post('/admin/users', payload);
    return data;
};

export const updateAdminUser = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/users/${id}`, payload);
    return data;
};

export const deleteAdminUser = async (id) => {
    await axiosInstance.delete(`/admin/users/${id}`);
};

export const approveAdminUserTermination = async (id) => {
    const { data } = await axiosInstance.post(`/admin/users/${id}/approve-termination`);
    return data;
};

export const fetchAdminRoles = async () => {
    const { data } = await axiosInstance.get('/admin/roles');
    return data;
};

export const fetchAdminOrganizations = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/organizations', { params });
    return data;
};

/**
 * Red za odobravanje registriranih tvrtki.
 *
 * Registracija vise nista ne aktivira sama: organizacija ceka potvrdu e-maila
 * pa nas pregled. Free trgovac prolazi automatski, pa ovdje zavrsavaju placeni
 * planovi te proizvodjaci i distributeri.
 */
/**
 * Javni prikaz partnera na landingu.
 *
 * Odvojeno od ostalih podataka o organizaciji jer je odluka druge vrste:
 * ostalo je opis, ovo je odluka da tvrtku javno spominjemo.
 */
export const updateOrganizationPublicPartner = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/organizations/${id}/public-partner`, payload);
    return data;
};

export const fetchOrganizationApprovals = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/organization-approvals', { params });
    return data;
};

export const fetchApprovalPlans = async () => {
    const { data } = await axiosInstance.get('/admin/organization-approvals/plans');
    return data;
};

export const approveOrganization = async (id, payload) => {
    const { data } = await axiosInstance.post(`/admin/organization-approvals/${id}/approve`, payload);
    return data;
};

export const rejectOrganization = async (id, reason) => {
    const { data } = await axiosInstance.post(`/admin/organization-approvals/${id}/reject`, { reason });
    return data;
};

export const fetchAdminOrganization = async (id) => {
    const { data } = await axiosInstance.get(`/admin/organizations/${id}`);
    return data;
};

export const createAdminOrganization = async (payload) => {
    const { data } = await axiosInstance.post('/admin/organizations', payload);
    return data;
};

export const updateAdminOrganization = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/organizations/${id}`, payload);
    return data;
};

export const deleteAdminOrganization = async (id) => {
    await axiosInstance.delete(`/admin/organizations/${id}`);
};

export const updateAdminOrganizationSettings = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/organizations/${id}/settings`, payload);
    return data;
};

export const fetchAdminOrganizationOverview = async (id) => {
    const { data } = await axiosInstance.get(`/admin/organization-ops/${id}/overview`);
    return data;
};

export const fetchAdminOrganizationVenues = async (id, params = {}) => {
    const { data } = await axiosInstance.get(`/admin/organization-ops/${id}/venues`, { params });
    return data;
};

export const fetchAdminOrganizationUsers = async (id, params = {}) => {
    const { data } = await axiosInstance.get(`/admin/organization-ops/${id}/users`, { params });
    return data;
};

export const fetchAdminOrganizationIntegrationsSummary = async (id) => {
    const { data } = await axiosInstance.get(`/admin/organization-ops/${id}/integrations-summary`);
    return data;
};

export const fetchAdminOrganizationCategoryMappings = async (id, params = {}) => {
    const { data } = await axiosInstance.get(`/admin/organizations/${id}/category-mappings`, { params });
    return data;
};

export const searchAdminOrganizationCategoryTaxonomy = async (id, query) => {
    const { data } = await axiosInstance.get(`/admin/organizations/${id}/category-mappings/taxonomy`, { params: { q: query } });
    return data;
};

export const updateAdminOrganizationCategoryMapping = async (id, mappingId, gpt_code) => {
    const { data } = await axiosInstance.put(`/admin/organizations/${id}/category-mappings/${mappingId}`, { gpt_code });
    return data;
};

export const autoMapAdminOrganizationCategories = async (id, source_type) => {
    const { data } = await axiosInstance.post(`/admin/organizations/${id}/category-mappings/auto-map`, { source_type });
    return data;
};

export const fetchAdminUserStatistics = async () => {
    const { data } = await axiosInstance.get('/admin/users/statistics');
    return data;
};

export const fetchAdminCustomers = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/customers', { params });
    return normalizeAdminUserList(data);
};

export const fetchAdminCustomer = async (id) => {
    const { data } = await axiosInstance.get(`/admin/customers/${id}`);
    if (data && typeof data === 'object') {
        return {
            ...data,
            data: normalizeAdminUser(data.data),
        };
    }
    return data;
};

export const fetchAdminCustomerStatistics = async () => {
    const { data } = await axiosInstance.get('/admin/customers/statistics');
    return data;
};

export const fetchAdminVenues = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/platform-venues', { params });
    return data;
};

export const fetchAdminVenue = async (id) => {
    const { data } = await axiosInstance.get(`/admin/platform-venues/${id}`);
    return data;
};

export const updateAdminVenue = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/platform-venues/${id}`, payload);
    return data;
};

export const fetchAdminVenueIntegration = async (id) => {
    const { data } = await axiosInstance.get(`/admin/platform-venues/${id}/integration`);
    return data;
};

export const updateAdminVenueIntegration = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/platform-venues/${id}/integration`, payload);
    return data;
};

// Scanned bills (system admin)
export const fetchAdminScannedBills = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/scanned-bills', { params });
    return data;
};

export const fetchAdminScannedBill = async (id) => {
    const { data } = await axiosInstance.get(`/admin/scanned-bills/${id}`);
    return data;
};

export const reviewAdminScannedBill = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/scanned-bills/${id}/review`, payload);
    return data;
};

export const rejectAdminScannedBill = async (id, payload) => {
    const { data } = await axiosInstance.post(`/admin/scanned-bills/${id}/reject`, payload);
    return data;
};

export const saveAdminScannedBillPreparedReviewState = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/scanned-bills/${id}/prepared-review-state`, payload);
    return data;
};

export const reprocessAdminScannedBill = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/admin/scanned-bills/${id}/reprocess-ocr`, payload);
    return data;
};

export const suggestAdminScannedBillWithAi = async (id, payload = { apply: true }) => {
    const { data } = await axiosInstance.post(`/admin/scanned-bills/${id}/ai-suggest`, payload);
    return data;
};

export const enrichAdminScannedBill = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/admin/scanned-bills/${id}/enrich`, payload);
    return data;
};

export const acceptAdminScannedBillEnrichment = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/admin/scanned-bills/${id}/enrich/accept`, payload);
    return data;
};

export const fetchAdminIntegrations = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/integrations', { params });
    return data;
};

export const fetchAdminIntegration = async (id) => {
    const { data } = await axiosInstance.get(`/admin/integrations/${id}`);
    return data;
};

export const fetchAdminIntegrationHealth = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/integrations/health', { params });
    return data;
};

export const fetchAdminIntegrationLogs = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/integrations/logs', { params });
    return data;
};

export const fetchAdminIntegrationLogDetail = async (id) => {
    const { data } = await axiosInstance.get(`/admin/integrations/logs/${id}`);
    return data;
};

export const fetchAdminIntegrationWebhooks = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/integrations/webhooks', { params });
    return data;
};

export const testAdminIntegrationWebhook = async (id) => {
    const { data } = await axiosInstance.post(`/admin/integrations/webhooks/${id}/test`);
    return data;
};

export const enableAdminIntegrationWebhook = async (id) => {
    const { data } = await axiosInstance.post(`/admin/integrations/webhooks/${id}/enable`);
    return data;
};

export const disableAdminIntegrationWebhook = async (id) => {
    const { data } = await axiosInstance.post(`/admin/integrations/webhooks/${id}/disable`);
    return data;
};

export const fetchAdminIntegrationSyncLogs = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/integrations/sync-logs', { params });
    return data;
};

export const fetchAdminIntegrationSyncItems = async (id) => {
    const { data } = await axiosInstance.get(`/admin/integrations/sync-logs/${id}/items`);
    return data;
};

export const fetchAdminIntegrationQuotas = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/integrations/quotas', { params });
    return data;
};

export const fetchAdminIntegrationInstallations = async () => {
    const { data } = await axiosInstance.get('/admin/integrations/installations');
    return data;
};

export const bootstrapAdminScannedBillEntities = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/admin/scanned-bills/${id}/bootstrap-entities`, payload);
    return data;
};

export const prepareAdminScannedBillReviewWithGemini = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(
        `/admin/scanned-bills/${id}/ai-prepare-review`,
        payload,
        { timeout: 600000 }
    );
    return data;
};

export const finalizeAdminScannedBill = async (id, payload = {}) => {
    const { data } = await axiosInstance.post(`/admin/scanned-bills/${id}/finalize`, payload);
    return data;
};

export const searchAdminScannedBillOrganizations = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/scanned-bills/lookups/organizations', { params });
    return data;
};

export const searchAdminScannedBillVenues = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/scanned-bills/lookups/venues', { params });
    return data;
};

export const fetchAdminScannedBillVenueTypes = async () => {
    const { data } = await axiosInstance.get('/admin/scanned-bills/lookups/venue-types');
    return data;
};

export const fetchAdminScannedBillWarrantyTemplateTypes = async () => {
    const { data } = await axiosInstance.get('/admin/scanned-bills/lookups/warranty-template-types');
    return data;
};

export const searchAdminScannedBillManufacturerItems = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/scanned-bills/lookups/manufacturer-items', { params });
    return data;
};

export const searchAdminScannedBillManufacturerVariants = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/scanned-bills/lookups/manufacturer-variants', { params });
    return data;
};

export const matchAdminScannedBillItem = async (id, itemId, payload) => {
    const { data } = await axiosInstance.put(`/admin/scanned-bills/${id}/items/${itemId}/match`, payload);
    return data;
};

export const fetchAdminScannedBillStats = async () => {
    const { data } = await axiosInstance.get('/admin/scanned-bills/statistics');
    return data;
};

export const fetchAdminUnmatchedScanItems = async () => {
    const { data } = await axiosInstance.get('/admin/scanned-bills/unmatched-items');
    return data;
};

export const bulkAdminScannedBills = async (payload) => {
    const { data } = await axiosInstance.post('/admin/scanned-bills/bulk-action', payload);
    return data;
};

export const uploadAdminGiveawayImage = async (file) => {
    const form = new FormData();
    form.append('image', file);
    const { data } = await axiosInstance.post('/admin/giveaways/image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
};

export const fetchAdminGiveaways = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/giveaways', { params });
    return data;
};

export const fetchAdminGiveaway = async (id) => {
    const { data } = await axiosInstance.get(`/admin/giveaways/${id}`);
    return data;
};

export const createAdminGiveaway = async (payload) => {
    const { data } = await axiosInstance.post('/admin/giveaways', payload);
    return data;
};

export const updateAdminGiveaway = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/giveaways/${id}`, payload);
    return data;
};

export const transitionAdminGiveaway = async (id, status, notes) => {
    const { data } = await axiosInstance.post(`/admin/giveaways/${id}/transition`, { status, notes });
    return data;
};

export const drawAdminGiveaway = async (id) => {
    const { data } = await axiosInstance.post(`/admin/giveaways/${id}/draw`);
    return data;
};

export const cancelAdminGiveaway = async (id, notes) => {
    const { data } = await axiosInstance.post(`/admin/giveaways/${id}/cancel`, { notes });
    return data;
};

export const fetchAdminGlobalPointsSettings = async () => {
    const { data } = await axiosInstance.get('/admin/global-points-settings');
    return data;
};

export const updateAdminGlobalPointsSettings = async (payload) => {
    const { data } = await axiosInstance.put('/admin/global-points-settings', payload);
    return data;
};

export const fetchAdminUserPointsHistory = async (userId, params = {}) => {
    const { data } = await axiosInstance.get(`/admin/users/${userId}/points/history`, { params });
    return data;
};

export const adjustAdminUserPoints = async (userId, payload) => {
    const { data } = await axiosInstance.post(`/admin/users/${userId}/points/adjust`, payload);
    return data;
};

export const reverseAdminUserPoints = async (userId, ledgerId, payload = {}) => {
    const { data } = await axiosInstance.post(`/admin/users/${userId}/points/reverse/${ledgerId}`, payload);
    return data;
};

// Scraper staging (system admin)
export const fetchAdminScraperStagingBatches = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/scraper-staging', { params });
    return data;
};

export const fetchAdminScraperStagingBatch = async (id, params = {}) => {
    const { data } = await axiosInstance.get(`/admin/scraper-staging/${id}`, { params });
    return data;
};

export const createAdminScraperStagingBatch = async (payload) => {
    const { data } = await axiosInstance.post('/admin/scraper-staging', payload);
    return data;
};

export const updateAdminScraperStagingRow = async (batchId, rowId, payload) => {
    const { data } = await axiosInstance.patch(`/admin/scraper-staging/${batchId}/rows/${rowId}`, payload);
    return data;
};

export const processAdminScraperStagingBatch = async (batchId, payload = {}) => {
    const { data } = await axiosInstance.post(`/admin/scraper-staging/${batchId}/process`, payload);
    return data;
};

// Catalogue sync and Gemini enrichment operations (system admin)
export const fetchCatalogOperationsOverview = async () => {
    const { data } = await axiosInstance.get('/admin/catalog-operations/overview');
    return data;
};

export const fetchAdminCatalogSources = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/catalog-operations/sources', { params });
    return data;
};

export const fetchAdminCatalogValueTransformer = async (code) => {
  const { data } = await axiosInstance.get(`/admin/catalog-operations/value-transformers/${encodeURIComponent(code)}`);
  return data?.data;
};

export const updateAdminCatalogSource = async (id, payload) => {
    const { data } = await axiosInstance.patch(`/admin/catalog-operations/sources/${id}`, payload);
    return data;
};

export const runAdminCatalogSource = async (id) => {
    const { data } = await axiosInstance.post(`/admin/catalog-operations/sources/${id}/run`);
    return data;
};

export const fetchAdminCatalogSyncRuns = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/catalog-operations/sync-runs', { params });
    return data;
};

export const fetchAdminCatalogEnrichmentRuns = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/catalog-operations/enrichment-runs', { params });
    return data;
};

export const fetchAdminCatalogEnrichmentProducts = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/catalog-operations/enrichment/products', { params });
    return data;
};

export const queueAdminCatalogEnrichment = async (manufacturerItemIds) => {
    const { data } = await axiosInstance.post('/admin/catalog-operations/enrichment/queue', { manufacturer_item_ids: manufacturerItemIds });
    return data;
};

export const retryAdminCatalogEnrichment = async (id) => {
    const { data } = await axiosInstance.post(`/admin/catalog-operations/enrichment-runs/${id}/retry`);
    return data;
};

export const fetchAdminScheduledCatalogOperations = async () => {
    const { data } = await axiosInstance.get('/admin/catalog-operations/scheduled-operations');
    return data;
};

export const updateAdminScheduledCatalogOperation = async (id, payload) => {
    const { data } = await axiosInstance.patch(`/admin/catalog-operations/scheduled-operations/${id}`, payload);
    return data;
};

export const runAdminScheduledCatalogOperation = async (id) => {
    const { data } = await axiosInstance.post(`/admin/catalog-operations/scheduled-operations/${id}/run`);
    return data;
};

export const fetchAdminScheduledCatalogOperationRuns = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/catalog-operations/scheduled-operation-runs', { params });
    return data;
};

// Promotion type builders
export const fetchConditionTypes = async () => {
    const { data } = await axiosInstance.get('/admin/promotion/types/conditions');
    return data;
};

export const createConditionType = async (payload) => {
    const { data } = await axiosInstance.post('/admin/promotion/types/conditions', payload);
    return data;
};

export const updateConditionType = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/promotion/types/conditions/${id}`, payload);
    return data;
};

export const deleteConditionType = async (id) => {
    await axiosInstance.delete(`/admin/promotion/types/conditions/${id}`);
};

export const fetchConditionAttributes = async (typeId) => {
    const { data } = await axiosInstance.get(`/admin/promotion/types/conditions/${typeId}/attributes`);
    return data;
};

export const createConditionAttribute = async (typeId, payload) => {
    const { data } = await axiosInstance.post(`/admin/promotion/types/conditions/${typeId}/attributes`, payload);
    return data;
};

export const updateConditionAttribute = async (typeId, attributeId, payload) => {
    const { data } = await axiosInstance.put(`/admin/promotion/types/conditions/${typeId}/attributes/${attributeId}`, payload);
    return data;
};

export const deleteConditionAttribute = async (typeId, attributeId) => {
    await axiosInstance.delete(`/admin/promotion/types/conditions/${typeId}/attributes/${attributeId}`);
};

export const fetchTargetTypes = async () => {
    const { data } = await axiosInstance.get('/admin/promotion/types/targets');
    return data;
};

export const createTargetType = async (payload) => {
    const { data } = await axiosInstance.post('/admin/promotion/types/targets', payload);
    return data;
};

export const updateTargetType = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/promotion/types/targets/${id}`, payload);
    return data;
};

export const deleteTargetType = async (id) => {
    await axiosInstance.delete(`/admin/promotion/types/targets/${id}`);
};

export const fetchTargetAttributes = async (typeId) => {
    const { data } = await axiosInstance.get(`/admin/promotion/types/targets/${typeId}/attributes`);
    return data;
};

export const createTargetAttribute = async (typeId, payload) => {
    const { data } = await axiosInstance.post(`/admin/promotion/types/targets/${typeId}/attributes`, payload);
    return data;
};

export const updateTargetAttribute = async (typeId, attributeId, payload) => {
    const { data } = await axiosInstance.put(`/admin/promotion/types/targets/${typeId}/attributes/${attributeId}`, payload);
    return data;
};

export const deleteTargetAttribute = async (typeId, attributeId) => {
    await axiosInstance.delete(`/admin/promotion/types/targets/${typeId}/attributes/${attributeId}`);
};

// Category mapper
export const fetchAdminCategoryMappings = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/categories/mappings', {
        params,
        headers: authHeaders(),
    });
    return data;
};

export const fetchAdminGptMappings = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/categories/mappings/gpt', {
        params,
        headers: authHeaders(),
    });
    return data;
};

export const fetchAdminUnspscMappings = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/categories/mappings/unspsc', {
        params,
        headers: authHeaders(),
    });
    return data;
};

export const fetchAdminIcecatMappings = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/categories/mappings/icecat', {
        params,
        headers: authHeaders(),
    });
    return data;
};

export const fetchAdminPendingCategoryMappings = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/categories/mappings/pending-categories', {
        params,
        headers: authHeaders(),
    });
    return data;
};

export const updateAdminCategoryMappings = async (payload) => {
    const { data } = await axiosInstance.patch('/admin/categories/mappings', payload, {
        headers: authHeaders(),
    });
    return data;
};

export const updateAdminGptMappings = async (payload) => {
    const { data } = await axiosInstance.patch('/admin/categories/mappings/gpt', payload, {
        headers: authHeaders(),
    });
    return data;
};

export const updateAdminUnspscMappings = async (payload) => {
    const { data } = await axiosInstance.patch('/admin/categories/mappings/unspsc', payload, {
        headers: authHeaders(),
    });
    return data;
};

export const updateAdminIcecatMappings = async (payload) => {
    const { data } = await axiosInstance.patch('/admin/categories/mappings/icecat', payload, {
        headers: authHeaders(),
    });
    return data;
};

export const updateAdminPendingCategoryMappings = async (payload) => {
    const { data } = await axiosInstance.patch('/admin/categories/mappings/pending-categories', payload, {
        headers: authHeaders(),
    });
    return data;
};

export const fetchAdminGpcCategories = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/categories/gpc', {
        params,
        headers: authHeaders(),
    });
    return data;
};

export const fetchAdminGptCategories = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/categories/gpt', {
        params,
        headers: authHeaders(),
    });
    return data;
};

export const fetchAdminUnspscCategories = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/categories/unspsc', {
        params,
        headers: authHeaders(),
    });
    return data;
};

export const fetchAdminIcecatCategories = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/categories/icecat', {
        params,
        headers: authHeaders(),
    });
    return data;
};

export const fetchIncentiveTypes = async () => {
    const { data } = await axiosInstance.get('/admin/promotion/types/incentives');
    return data;
};

export const createIncentiveType = async (payload) => {
    const { data } = await axiosInstance.post('/admin/promotion/types/incentives', payload);
    return data;
};

export const updateIncentiveType = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/promotion/types/incentives/${id}`, payload);
    return data;
};

export const deleteIncentiveType = async (id) => {
    await axiosInstance.delete(`/admin/promotion/types/incentives/${id}`);
};

export const fetchIncentiveAttributes = async (typeId) => {
    const { data } = await axiosInstance.get(`/admin/promotion/types/incentives/${typeId}/attributes`);
    return data;
};

export const createIncentiveAttribute = async (typeId, payload) => {
    const { data } = await axiosInstance.post(`/admin/promotion/types/incentives/${typeId}/attributes`, payload);
    return data;
};

export const updateIncentiveAttribute = async (typeId, attributeId, payload) => {
    const { data } = await axiosInstance.put(`/admin/promotion/types/incentives/${typeId}/attributes/${attributeId}`, payload);
    return data;
};

export const deleteIncentiveAttribute = async (typeId, attributeId) => {
    await axiosInstance.delete(`/admin/promotion/types/incentives/${typeId}/attributes/${attributeId}`);
};

export const fetchStrategyTypes = async () => {
    const { data } = await axiosInstance.get('/admin/promotion/types/strategies');
    return data;
};

export const createStrategyType = async (payload) => {
    const { data } = await axiosInstance.post('/admin/promotion/types/strategies', payload);
    return data;
};

export const updateStrategyType = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/promotion/types/strategies/${id}`, payload);
    return data;
};

export const deleteStrategyType = async (id) => {
    await axiosInstance.delete(`/admin/promotion/types/strategies/${id}`);
};

export const fetchStrategyAttributes = async (typeId) => {
    const { data } = await axiosInstance.get(`/admin/promotion/types/strategies/${typeId}/attributes`);
    return data;
};

export const createStrategyAttribute = async (typeId, payload) => {
    const { data } = await axiosInstance.post(`/admin/promotion/types/strategies/${typeId}/attributes`, payload);
    return data;
};

export const updateStrategyAttribute = async (typeId, attributeId, payload) => {
    const { data } = await axiosInstance.put(`/admin/promotion/types/strategies/${typeId}/attributes/${attributeId}`, payload);
    return data;
};

export const deleteStrategyAttribute = async (typeId, attributeId) => {
    await axiosInstance.delete(`/admin/promotion/types/strategies/${typeId}/attributes/${attributeId}`);
};

// Cities API
export const fetchAdminCities = async (params = {}) => {
    const { data } = await axiosInstance.get('/admin/cities', { params });
    return data;
};

export const fetchAdminCity = async (id) => {
    const { data } = await axiosInstance.get(`/admin/cities/${id}`);
    return data;
};

export const fetchAdminCityStatistics = async () => {
    const { data } = await axiosInstance.get('/admin/cities/statistics');
    return data;
};

export const createAdminCity = async (payload) => {
    const { data } = await axiosInstance.post('/admin/cities', payload);
    return data;
};

export const updateAdminCity = async (id, payload) => {
    const { data } = await axiosInstance.put(`/admin/cities/${id}`, payload);
    return data;
};

export const deleteAdminCity = async (id) => {
    await axiosInstance.delete(`/admin/cities/${id}`);
};

export const sendAdminUserPushNotification = async (payload) => {
    const { data } = await axiosInstance.post('/admin/notifications/send-user-push', payload, {
        headers: authHeaders(),
    });
    return data;
};
